import { ProcedureRow } from "types/CreateOrder";

import {
  buildFilteredProceduresSuggestions,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./builders";
import {
  getCptQuantities,
  getNextInternalId,
  replicateByCptQuantity,
} from "./helpers";

export type PayerChangeKind = "hydration" | "unchanged" | "changed";

/**
 * Tells the payer that just hydrated on a redo/draft/copy (which must not rebuild the table
 * — `initializeDefault` is still seeding it) from the user picking a different payer (which
 * must, or the rows keep the previous payer's providerProcedureIds). CLE-2538 follow-up.
 */
export const classifyPayerChange = ({
  previousPayer,
  nextPayer,
}: {
  previousPayer: string | null | undefined;
  nextPayer: string | null | undefined;
}): PayerChangeKind => {
  // Recording an empty payer would make the real value arriving later look like a change.
  if (!nextPayer) return "unchanged";
  if (!previousPayer) return "hydration";
  if (previousPayer === nextPayer) return "unchanged";
  return "changed";
};

/**
 * CPT codes the new payer does not carry produce no rebuilt row, so a plain replace deletes
 * those line items out from under the user. Keep them, flagged, and let the user decide.
 */
export const preserveUnavailableRows = ({
  rebuiltRows,
  previousRows,
  unavailableCptCodes,
}: {
  rebuiltRows: ProcedureRow[];
  previousRows: ProcedureRow[];
  unavailableCptCodes: string[];
}): ProcedureRow[] => {
  const available = rebuiltRows.map((row) => ({
    ...row,
    notAvailableForPayer: false,
  }));

  const unavailable = new Set(unavailableCptCodes);
  if (!unavailable.size) return available;

  // internalId groups the replicas of one line. Rebuilt rows are numbered from 0, so
  // preserved rows must be renumbered above them — while rows that shared an id still do.
  let nextInternalId = getNextInternalId(available);
  const remapped = new Map<number, number>();

  const preserved = previousRows
    .filter((row) => unavailable.has(row.cptCode))
    .map((row) => {
      if (!remapped.has(row.internalId)) {
        remapped.set(row.internalId, nextInternalId);
        nextInternalId += 1;
      }
      return {
        ...row,
        internalId: remapped.get(row.internalId) as number,
        notAvailableForPayer: true,
      };
    });

  return [...available, ...preserved];
};

/**
 * `autoCompleteValue` holds one entry per *unit* — quantity is never persisted, the UI
 * re-derives it by counting duplicates. Without collapsing, the search fires one request per
 * unit and the rebuild replicates by quantity a second time, so a quantity-N line came back
 * as N * N rows. CLE-2538 fixed the same fan-out in the page initializers but not here.
 */
export const distinctByCptCode = (autoCompleteValue: string[]): string[] => {
  const seen = new Set<string>();

  return autoCompleteValue.filter((value) => {
    const [{ code }] = buildSelectedProceduresObject([value]);
    if (seen.has(code)) return false;
    seen.add(code);
    return true;
  });
};

/**
 * Fetch this account's provider procedures for a payer + visit date and rebuild the
 * procedure table from them. Pure in / out apart from `search`; the caller decides whether
 * to apply `nextRows` (a payer change must, a visit-date change on redo/draft/copy must not
 * — amounts may have been hand-edited on the original order).
 */
export const fetchPayerProcedures = async ({
  accountId,
  payerName,
  dateOfService,
  autoCompleteValue,
  getPreviousRows,
  search,
}: {
  accountId: string;
  payerName: string;
  dateOfService?: string;
  autoCompleteValue: string[];
  /** Read after the await: the table may have been seeded while the request was in flight. */
  getPreviousRows: () => ProcedureRow[];
  search: (args: {
    accountId: string;
    cptCode: string;
    payerName: string;
    dateOfService?: string;
  }) => Promise<any>;
}): Promise<{ unavailableCptCodes: string[]; nextRows: ProcedureRow[] }> => {
  const distinctValue = distinctByCptCode(autoCompleteValue);
  const cptCodesToFetch = buildSelectedProceduresObject(distinctValue).map(
    (item) => item.code
  );

  const responses = await Promise.all(
    cptCodesToFetch.map(async (cptCode) => {
      const response = await search({
        accountId,
        cptCode,
        payerName,
        ...(dateOfService ? { dateOfService } : {}),
      });
      return response?.data?.data?.data || [];
    })
  );

  const providerProcedures = responses.flat();
  const procedures = providerProcedures.map(
    (providerProcedure: any) => providerProcedure.procedure || []
  );
  const availableSuggestions = buildFilteredProceduresSuggestions(procedures);

  const availableValue = distinctValue.filter((value) =>
    availableSuggestions.includes(value)
  );
  const unavailableCptCodes = buildSelectedProceduresObject(
    distinctValue.filter((value) => !availableSuggestions.includes(value))
  ).map((item) => item.code);

  const previousRows = getPreviousRows();
  const rebuiltRows = buildSelectedProceduresToTable({
    selectedProceduresObject: replicateByCptQuantity(
      buildSelectedProceduresObject(availableValue),
      getCptQuantities(previousRows)
    ),
    procedures,
    providerProcedures,
    rows: [],
    row: undefined,
  });

  return {
    unavailableCptCodes,
    nextRows: preserveUnavailableRows({
      rebuiltRows,
      previousRows,
      unavailableCptCodes,
    }),
  };
};
